import { SvelteURL } from 'svelte/reactivity'
import type { slideInModes } from '$mapapp/data/data.model'
import { miniLotSchema, type MiniLot } from '$mapapp/types/miniLot'
import { compressToBase64Url, decompressFromBase64Url } from '$utils/compress'
import { MATCH_URL_ENTRY_TYPES } from '$mapapp/config'
import { appState } from './appstate.svelte'
import { fsm } from './fsm.svelte'
import type { MatchingOptions } from './matching.svelte'

/** Marker types that support URL state */
export const MARKER_PARAMS: slideInModes[] = ['pop', 'lot', 'project']
type DeepLinkMarker = { type: slideInModes; markerId: string; referenceId: string }

/**
 * parse URL deep-link for single-marker fetch (`requestSingleMarker`)
 *
 * checks `?pop=` / `?lot=` / `?project=` first, then `?match=lot|pop`.
 * lowercases legacy uppercased IDs. null for miniLot (no marker) and unrecognized values.
 * map keys use lowercased `lot-1450`; backend wants bare `1450`.
 */
export function getDeepLinkMarker(searchParams: URLSearchParams): DeepLinkMarker | null {
	for (const type of MARKER_PARAMS) {
		const rawMarkerId = searchParams.get(type)
		if (!rawMarkerId) continue
		const markerId = rawMarkerId.toLowerCase()
		const prefix = `${type}-`
		if (!markerId.startsWith(prefix)) return null
		const referenceId = markerId.slice(prefix.length)
		if (!referenceId) return null
		return { type, markerId, referenceId }
	}

	const rawMatch = searchParams.get('match')
	if (!rawMatch) return null
	const markerId = rawMatch.toLowerCase()
	if (markerId === 'minilot') return null
	for (const type of MATCH_URL_ENTRY_TYPES) {
		const prefix = `${type}-`
		if (!markerId.startsWith(prefix)) continue
		const referenceId = markerId.slice(prefix.length)
		if (!referenceId) return null
		return { type, markerId, referenceId }
	}
	return null
}

function updateURL(url: SvelteURL) {
	if (url.href !== window.location.href) {
		window.history.replaceState({}, '', url.href)
	}
}

function setParam(key: string, value: string) {
	const url = new SvelteURL(window.location.href)
	url.searchParams.set(key, value)
	updateURL(url)
}

function deleteParam(key: string) {
	const url = new SvelteURL(window.location.href)
	url.searchParams.delete(key)
	updateURL(url)
}

/** Sync marker param to URL - clears all other marker params first */
export function syncMarkerParam(type: slideInModes, id: string) {
	const url = new SvelteURL(window.location.href)
	for (const param of MARKER_PARAMS) {
		url.searchParams.delete(param)
	}
	url.searchParams.set(type, id)
	updateURL(url)
}

/** Clear all marker params */
export function clearAllMarkerParams() {
	const url = new SvelteURL(window.location.href)
	for (const param of MARKER_PARAMS) {
		url.searchParams.delete(param)
	}
	updateURL(url)
}

/**
 * Parse the `radius` param into a matching radius range. The param carries the `to` bound (km);
 * `from` is always 0 (see {@link syncRadiusParam}). Returns undefined for a missing or invalid
 * value so callers fall back to the default radius. `radius` is attacker-controllable (shareable
 * URL) — reject anything non-finite or ≤ 0.
 */
export function getRadiusParam(
	searchParams: URLSearchParams
): { from: number; to: number } | undefined {
	const raw = searchParams.get('radius')
	if (raw === null) return undefined
	const to = Number(raw)
	if (!Number.isFinite(to) || to <= 0) return undefined
	return { from: 0, to }
}

/** Sync radius param to URL — positions it before `ml` so minilot URLs keep `?radius=…&ml=…` order */
export function syncRadiusParam(radiusTo: number) {
	const url = new SvelteURL(window.location.href)
	const ml = url.searchParams.get('ml')
	url.searchParams.delete('ml')
	url.searchParams.set('radius', String(radiusTo))
	if (ml !== null) {
		url.searchParams.set('ml', ml)
	}
	updateURL(url)
}

/** Sync FAK params to URL */
export function syncFakParams(projectId: string | null, lotId: string | null) {
	const url = new SvelteURL(window.location.href)
	if (projectId) {
		url.searchParams.set('formProjectId', projectId)
		if (lotId) {
			url.searchParams.set('formLotId', lotId)
		} else {
			url.searchParams.delete('formLotId')
		}
	} else {
		url.searchParams.delete('formProjectId')
		url.searchParams.delete('formLotId')
	}
	updateURL(url)
}

/** Handle FAK URL params - can run immediately (doesn't need markers loaded) */
export function handleFakUrlParams(searchParams: URLSearchParams): boolean {
	const formProjectId = searchParams.get('formProjectId')
	if (formProjectId) {
		fsm.send('set_fak', { projectId: formProjectId, lotId: searchParams.get('formLotId') })
		return true
	}
	return false
}

/** selects marker and flies to it; lowercases legacy uppercased IDs before FSM dispatch */
export function handleMarkerUrlParam(type: slideInModes, id: string) {
	fsm.send(`set_${type}`, { id: id.toLowerCase(), flyTo: true })
}

/**
 * entry point matching from `?match=`
 * lowercases legacy uppercased IDs
 * HINT: miniLot has no marker: use handleMiniLotMatchUrlParam.
 */
export function handleMatchUrlParam(
	matchId: string,
	profileId?: string | null,
	radius?: { from: number; to: number }
) {
	if (!matchId) return
	const normalized = matchId.toLowerCase()
	const marker = appState.allMarkers.get(normalized)
	if (!marker) return
	const type = MATCH_URL_ENTRY_TYPES.find((entryType) => entryType === marker.type)
	if (!type) return
	const prefix = `${type}-`
	if (!normalized.startsWith(prefix)) return
	const id = normalized.slice(prefix.length)
	if (!id) return
	const options: MatchingOptions = {}
	if (profileId) options.profileId = profileId
	if (radius) options.radius = radius
	appState.startMatching({ id, type, options: Object.keys(options).length ? options : undefined })
}

/** Sync match param to URL — clears marker params first, optionally sets profileId.
 *  For a miniLot match the miniLot is encoded into the `ml` param so reload can restore it. */
export function syncMatchParam(id: string, profileId?: string, miniLot?: MiniLot) {
	clearAllMarkerParams()
	setParam('match', id)
	if (miniLot) {
		setParam('ml', compressToBase64Url(miniLot))
	} else {
		deleteParam('ml')
	}
	if (profileId) {
		setParam('profileId', profileId)
	} else {
		deleteParam('profileId')
	}
}

/** Clear match param, profileId and the encoded miniLot from URL */
export function clearMatchParam() {
	deleteParam('match')
	deleteParam('profileId')
	deleteParam('ml')
	deleteParam('partner')
	deleteParam('radius')
}

/** Restore a miniLot match from the URL — the miniLot is encoded in `ml` because
 *  it has no marker to look up. Runs at startup (does not need markers loaded). */
export function handleMiniLotMatchUrlParam(searchParams: URLSearchParams): boolean {
	if (searchParams.get('match')?.toLowerCase() !== 'minilot') return false
	const encoded = searchParams.get('ml')
	if (!encoded) return false
	const miniLot = decompressFromBase64Url<MiniLot>(encoded)
	// `ml` is attacker-controllable (shareable URL) — reject anything that isn't a valid MiniLot.
	if (!miniLot || !miniLotSchema.isValidSync(miniLot)) return false
	appState.setMinilotData(miniLot)
	const radius = getRadiusParam(searchParams)
	appState.startMatching({
		id: '',
		type: 'miniLot',
		options: radius ? { miniLot, radius } : { miniLot }
	})
	return true
}
